import React, { useState } from 'react';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faCalendarAlt } from '@fortawesome/pro-light-svg-icons/faCalendarAlt';
import PropTypes from 'prop-types';
import { conjunct, ordinalize, sequence } from '../../utils';
import FontAwesome from '../../utils/FA';
import { billingAccountForm, billingItems, autopayInstallments, modals } from '../../ducks';
import { duckify } from '../../utils/duckify';
import './styles/AutopayDateEditor.scss';

export const ordinalizeBillingDay = (num) => ((num === 32 || num === '32') ? 'Last Day' : ordinalize(num));

export const selectableBillingDays = () => {
  const seq = sequence(1, 28);
  seq.push(32);
  return seq;
};

export const formatBillingDays = (billingDays) => `${conjunct(billingDays.map(ordinalizeBillingDay))} of the month`;

export const AutopayDateDisplay = ({
  billingAccount, showExpiringDays, hideIcon, daysCss
}) => {
  const days = showExpiringDays ? billingAccount.expiring_autopay_days : billingAccount.autopay_days;
  if (days.length <= 0) {
    return <React.Fragment />;
  }
  const display = !billingAccount.autopay ? 'Billing' : 'Autopay Billing';

  const additionalInfo = showExpiringDays
    ? ' this month'
    : `${billingAccount.new_schedule_start ? ` starting ${billingAccount.new_schedule_start}` : ''}`;

  return (
    <React.Fragment>
      {!hideIcon && <FontAwesome icon={faCalendarAlt} nbsp />}
      {`${display} Day(s)${additionalInfo}: `}
      <span className={daysCss}>
        {formatBillingDays(days)}
      </span>
    </React.Fragment>
  );
};
AutopayDateDisplay.propTypes = {
  billingAccount: PropTypes.shape({
    autopay: PropTypes.bool,
    new_schedule_start: PropTypes.string,
    expiring_autopay_days: PropTypes.array,
    autopay_days: PropTypes.array
  }).isRequired,
  /* whether to show expiring days or days going forward */
  showExpiringDays: PropTypes.bool,
  hideIcon: PropTypes.bool,
  /* css for the span containing the days */
  daysCss: PropTypes.string
};
AutopayDateDisplay.defaultProps = {
  showExpiringDays: false,
  hideIcon: false,
  daysCss: ''
};

const AutopayDateEditor = ({ billingAccount, billingConfig, isAdmin, changePlan }) => {
  const [adding, setAdding] = useState(false);

  return (
    <div className='billing-account-autopay-editor'>
      {adding
        ? <InternalAutopayDateEditor billingAccount={billingAccount} billingConfig={billingConfig} changePlan={changePlan} setAdding={setAdding} isAdmin={isAdmin} />
        : (
          <button type='button' className='btn btn-primary' onClick={() => setAdding(true)}>
            {billingAccount.autopay ? 'Change Autopay Days' : 'Change Billing Day'}
          </button>
        )
      }
    </div>
  );
};
AutopayDateEditor.propTypes = {
  billingAccount: PropTypes.shape({
    autopay: PropTypes.bool,
    new_schedule_start: PropTypes.string,
    expiring_autopay_days: PropTypes.array,
    autopay_days: PropTypes.array
  }).isRequired,
  billingConfig: PropTypes.shape({}).isRequired,
  isAdmin: PropTypes.bool,
  changePlan: PropTypes.func.isRequired
};
AutopayDateEditor.defaultProps = {
  isAdmin: false
};

const InternalAutopayDateEditor = ({ billingAccount, billingConfig, isAdmin, changePlan, setAdding }) => {
  const [submitting, setSubmitting] = useState(false);
  const [selectedAutopayPlan, setSelectedAutopayPlan] = useState(billingAccount.selected_autopay_plan);
  const [selectedSingle, setSelectedSingle] = useState(billingAccount.selected_autopay_plan === 'single' ? billingAccount.billing_day : null);
  const [selectedBi, setSelectedBi] = useState((billingAccount.selected_autopay_plan === 'bi_monthly' && isAdmin) ? billingAccount.autopay_days : billingConfig.bi_monthly_default);
  const [selectedFour, setSelectedFour] = useState((billingAccount.selected_autopay_plan === 'four_monthly' && isAdmin) ? billingAccount.autopay_days : billingConfig.four_monthly_default);
  const billingDays = billingConfig.billing_days;

  const updateSelectedBi = (index, value) => {
    const newBi = selectedBi.slice();
    newBi[index] = value;
    setSelectedBi(newBi);
  };

  const updateSelectedFour = (index, value) => {
    const newFour = selectedFour.slice();
    newFour[index] = value;
    setSelectedFour(newFour);
  };

  return (
    <React.Fragment>
      <div>
        <div className='billing-account-autopay-plan'>
          <input
            disabled={submitting}
            name='selected_autopay_plan_single'
            type='radio'
            value='single'
            checked={selectedAutopayPlan === 'single'}
            onChange={(e) => setSelectedAutopayPlan(e.target.value)}
          />
          { 'Single Billing Day' }
          <select
            value={selectedSingle}
            onChange={(e) => setSelectedSingle(e.target.value)}
            name='single_day'
            className='form-select form-select-sm'
          >
            <option key='default' value=''>
              {billingConfig.ordinalized_billing_day}
              {' '}
              (Default)
            </option>
            {billingDays.map(i => <option key={`${i}-default`} value={i}>{ordinalizeBillingDay(i)}</option>)}
            {isAdmin && selectableBillingDays().map(i => <option key={i} value={i}>{ordinalizeBillingDay(i)}</option>)}
          </select>
        </div>
        {(billingAccount.autopay && billingConfig.offer_bi_monthly && (billingConfig.show_parents_bi_monthly || isAdmin)) && (
          <div className='billing-account-autopay-plan'>
            <input
              disabled={submitting}
              name='selected_autopay_plan_bi_monthly'
              type='radio'
              value='bi_monthly'
              checked={selectedAutopayPlan === 'bi_monthly'}
              onChange={(e) => setSelectedAutopayPlan(e.target.value)}
            />
            <div className='autopay-plan-description'>
              <span>2 Monthly Withdrawals</span>
              {!isAdmin && <span className='helper-text'>{formatBillingDays(selectedBi)}</span>}
            </div>
            { isAdmin && ([0, 1].map((index) => (
              <select
                value={selectedBi[index]}
                name={`bi_monthly_${index}`}
                onChange={(e) => updateSelectedBi(index, e.target.value)}
                className='form-select form-select-sm'
              >
                {selectableBillingDays().map(i => <option key={i} value={i}>{ordinalizeBillingDay(i)}</option>)}
              </select>
            )))}
          </div>
        )}
        {(billingAccount.autopay && billingConfig.offer_four_monthly && (billingConfig.show_parents_four_monthly || isAdmin)) && (
          <div className='billing-account-autopay-plan'>
            <input
              disabled={submitting}
              name='selected_autopay_plan_four_monthly'
              type='radio'
              value='four_monthly'
              checked={selectedAutopayPlan === 'four_monthly'}
              onChange={(e) => setSelectedAutopayPlan(e.target.value)}
            />
            <div className='autopay-plan-description'>
              <span>4 Monthly Withdrawals</span>
              {!isAdmin && <span className='helper-text'>{formatBillingDays(selectedFour)}</span>}
            </div>
            { isAdmin && ([0, 1, 2, 3].map((index) => (
              <select
                value={selectedFour[index]}
                name={`four_monthly_${index}`}
                onChange={(e) => updateSelectedFour(index, e.target.value)}
                className='form-select form-select-sm'
              >
                {selectableBillingDays().map(i => <option key={i} value={i}>{ordinalizeBillingDay(i)}</option>)}
              </select>
            )))}
          </div>
        )}

        <div className='update-autopay-dates-buttons'>
          <div className='input-group-btn'>
            <button
              type='button'
              className='btn btn-light'
              disabled={submitting}
              onClick={() => {
                setAdding(false);
              }}
            >
              <FontAwesome icon={faTimes} />
            </button>

            <button
              type='button'
              className='btn btn-primary'
              disabled={submitting}
              onClick={
                () => {
                  setSubmitting(true);

                  changePlan(
                    {
                      selectedAutopayPlan, selectedSingle, selectedBi, selectedFour
                    },
                    isAdmin,
                    billingConfig.billing_day,
                    setAdding,
                    setSubmitting
                  );
                }
              }
            >
              {
                submitting && <FontAwesome icon={faSpinner} spin />
              }
              {
                !submitting && <FontAwesome icon={faCheck} />
              }
            </button>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
InternalAutopayDateEditor.propTypes = {
  billingAccount: PropTypes.shape({
    autopay: PropTypes.bool,
    new_schedule_start: PropTypes.string,
    expiring_autopay_days: PropTypes.array,
    autopay_days: PropTypes.array,
    selected_autopay_plan: PropTypes.string,
    billing_day: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired,
  billingConfig: PropTypes.shape({
    billing_day: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ordinalized_billing_day: PropTypes.string,
    billing_days: PropTypes.array,
    bi_monthly_default: PropTypes.array,
    four_monthly_default: PropTypes.array,
    offer_bi_monthly: PropTypes.bool,
    offer_four_monthly: PropTypes.bool,
    show_parents_bi_monthly: PropTypes.bool,
    show_parents_four_monthly: PropTypes.bool
  }).isRequired,
  isAdmin: PropTypes.bool,
  changePlan: PropTypes.func.isRequired,
  setAdding: PropTypes.func.isRequired
};
InternalAutopayDateEditor.defaultProps = {
  isAdmin: false
};

const mapDispatchToProps = (dispatch, { billingAccount }) => ({
  changePlan: (model, isAdmin, defaultDay, setAdding, setSubmitting) => {
    let billingDays;
    if (model.selectedAutopayPlan === 'bi_monthly') {
      billingDays = model.selectedBi;
    } else if (model.selectedAutopayPlan === 'four_monthly') {
      billingDays = model.selectedFour;
    } else {
      billingDays = model.selectedSingle ? [model.selectedSingle] : [];
    }
    const saveBillingDays = () => {
      dispatch(billingAccountForm.actions.save({
        id: billingAccount.id,
        billing_days: billingDays
      })).then(() => {
        dispatch(billingItems.actions.markDirty());
        dispatch(autopayInstallments.actions.markDirty());
        document.activeElement.blur();
      }).then(() => {
        setSubmitting(false);
        setAdding(false);
      }).catch(response => {
        setSubmitting(false);
        if (response.billing_account && response.billing_account.errors && response.billing_account.errors.autopay_days) {
          Utility.displayFlashMessages(
            { error: response.billing_account.errors.autopay_days.map(error => `Autopay days ${error}`).join(' ') }
          );
        } else {
          Utility.displayFlashMessages(
            { error: 'Sorry, something went wrong.' }
          );
        }
      });
    };

    if (isAdmin) {
      saveBillingDays();
    } else {
      const daysText = billingDays.length > 0
        ? formatBillingDays(billingDays) : formatBillingDays([defaultDay]);
      const message = `You have selected to change your autopay withdrawal dates to the ${daysText}. ${
        (billingDays.length > 1)
          ? `All contract fees will be spread across the ${daysText} specified on your contract. Installment charges will be withdrawn on autopay dates based on the charge post date. Review billing ledger for exact amounts.`
          : 'All monthly contract installment charges will be collected on this date along with any added incidentals due.'
      } Some autopay changes do not take effect until the following month. Review your ledger after making a change.`;

      dispatch(modals.actions.show('VerificationModal', {
        title: 'Confirm Autopay Date Change',
        message,
        onClose: () => {
          setSubmitting(false);
          dispatch(modals.actions.hide());
        },
        onConfirm: () => saveBillingDays()
      }));
    }
  }
});

export default duckify(() => ({}), () => ({}), mapDispatchToProps)(AutopayDateEditor);
